/**
 * Design tokens for the `customizer` wizard ("Take your website live").
 *
 * Mirrors the `configurator-tokens.ts` split: a `CUST` palette + grouped
 * gradient / chrome / animation maps, re-using the area's Satoshi brand font.
 * This is the only place raw colour literals (incl. alpha tints + gradients)
 * for the wizard may live — components import from here.
 *
 * THEMING: like the area's `tokens.raw.ts` → `tokens.ts` split, every
 * colour-bearing export below is wrapped through the theming registry
 * (`themed()` from `themeRegistry.ts`) so each value resolves to a
 * `var(--ows-cust-…)` custom property instead of a hard literal. The literals
 * captured here ARE the dark theme (byte-for-byte as before); the light values
 * are derived/overridden in `themePalette.ts` and emitted by `buildThemeCss.ts`.
 * Consumers import the SAME names and need no change — switching light ⇄ dark is
 * then pure CSS (the `<html>` element carries `data-ows-theme`). `customizer-tokens`
 * is side-effect-imported by `buildThemeCss.ts` so its vars register before the
 * stylesheet builds.
 *
 * Values are pulled verbatim from Figma (Owner-Studio "Step 1: Operating Area"
 * desktop + Spark-Configurator mobile). The desktop design marks some headings
 * "Circular Std"; that face is not shipped by the shell, so — like the sibling
 * configurator-v3 — the wizard standardises on Satoshi (`CUST_FONT`).
 */

/* eslint-disable quotes -- SVG data-URI gradients need both ' and " */

import { themed } from './themeRegistry';

/** The wizard's brand font stack (Satoshi is loaded globally in index.html). */
export const CUST_FONT = 'Satoshi, ui-sans-serif, system-ui, sans-serif';

/** Mobile switch — the wizard flips to its phone flow at/below this width
 *  (matches the source Deploy wizard's `(max-width: 899px)`). */
export const CUST_MOBILE = '@media (max-width: 899px)';

/** Reduced-motion opt-out — components drop non-essential transitions under it. */
export const CUST_REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const CUST_RAW = {
  // Surfaces ------------------------------------------------------------------
  pageBg: '#08080a',
  // Dropdown trigger (Figma Input): zinc-900 fill, 2px zinc-900 ring, r12.
  fieldBg: '#18181b',
  fieldBorder: '#18181b',
  // Right "Live progress" panel: 6% white over 64% near-black, white hairline, r16.
  panelBorder: 'rgba(255, 255, 255, 0.1)',
  panelDivider: '#2e2e31',
  // Mobile bottom-sheet / sticky CTA scrim base.
  surfaceCard: '#18181b',

  // Text ----------------------------------------------------------------------
  white: '#ffffff',
  textLabel: '#52525b', // field label (default-600)
  textPlaceholder: '#a1a1aa', // field value placeholder / step labels (default-400)
  textMuted: '#a1a1aa',
  textFaint: '#71717a',
  required: '#f31260', // "*" (danger)

  // Accent (tenant orange) ----------------------------------------------------
  accent: '#ff791b',
  accentTint: 'rgba(255, 121, 27, 0.2)', // "In progress" chip bg
  onAccent: '#000000',
  // Brand orange used as TEXT / icon (eyebrows, step headings, "In progress"
  // label, inline links). Same orange in dark; on LIGHT it deepens to a burnt
  // orange that clears AA on the white surface (raw #ff791b is only ~2.6:1 as
  // text on white) — use this (NOT `accent`) for any orange label/heading/link,
  // and keep `accent` for fills/borders/CTA backgrounds. Light value lives in
  // themePalette's LIGHT_OVERRIDES. Mirrors configurator's `CFG.brandText`.
  accentText: '#ff791b',

  // Progress bar --------------------------------------------------------------
  progressTrack: '#3f3f46', // foreground-700
  progressFill: '#ff791b',

  // Continue / CTA button -----------------------------------------------------
  ctaDisabledBg: 'rgba(212, 212, 216, 0.4)', // default-flat (no selection yet)
  ctaText: '#000000', // default-foreground

  // Ask Lumio pill ------------------------------------------------------------
  lumioBorder: '#ff00d4',
  lumioGlyph: '#00e0ff',

  // World map -----------------------------------------------------------------
  mapBase: 'rgba(255, 255, 255, 0.08)', // dimmed country fill
  mapStroke: 'rgba(255, 255, 255, 0.05)',
  mapSelected: '#338ef7', // highlighted operating country (Figma: blue, not orange)
  // Map frame: same translucent plate + white hairline as the progress panel,
  // r16, clipped so a zoom-in never spills outside (Figma "Frame 43248").
  mapFrameBorder: 'rgba(255, 255, 255, 0.1)',

  // Header --------------------------------------------------------------------
  headerAvatarBg: 'rgba(255, 255, 255, 0.06)',
  headerAvatarBgHover: 'rgba(255, 255, 255, 0.12)',

  // Dropdown open menu --------------------------------------------------------
  menuBg: '#18181b',
  menuBorder: 'rgba(255, 255, 255, 0.08)',
  rowHover: 'rgba(255, 255, 255, 0.06)',
  flagRing: 'rgba(255, 255, 255, 0.12)',
  comingSoon: '#a1a1aa',
  soonPillBg: 'rgba(255, 255, 255, 0.1)',
  rowCurrency: '#52525b', // right-aligned currency code in each row (default-600)
  // Dropdown open-menu elevation.
  menuShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.6)',

  // Mobile flow ---------------------------------------------------------------
  mobileHairline: 'rgba(255, 255, 255, 0.08)',
  stageNumIdle: '#3f3f46', // inactive stage circle bg
  stageNumIdleTransparent: 'rgba(63, 63, 70, 0)', // same grey at 0 alpha — the morph globe plate dissolves to THIS (fades the same hue out instead of cross-fading to black)
  stageNumIdleText: '#ffffff', // idle stage ink (2 · 3 · globe): pure white on dark, pure black on light (override in themePalette)
  introSub: '#e7e5e4', // intro subtitle (stone-200)
  mobileTrack: 'rgba(255, 255, 255, 0.2)', // progress-bar track (mobile chrome)

  // Step 2 — name & domain suggestions -----------------------------------------
  eyebrowText: '#e4e4e7', // "Lumio's suggestions for …" eyebrow + unselected card title
  // Suggestion cards/rows sit on a SOLID dark surface (the Figma translucent
  // plate composites too dark over the customizer's near-black page) with a soft
  // white hairline; the selected one swaps to an orange ring + glow.
  cardSurface: '#141417',
  cardBorder: 'rgba(255, 255, 255, 0.16)', // unselected suggestion card/row border
  freeColor: '#17c964', // domain "Free" price (success)
  doneChipBg: 'rgba(23, 201, 100, 0.2)', // progress "Done" chip bg
  doneChipText: '#17c964', // progress "Done" chip + completed-step text
  skeletonBg: 'rgba(255, 255, 255, 0.06)',
  skeletonShine: 'rgba(255, 255, 255, 0.14)',

  // Step 2 — connect-domain sub-flow ("Link your own domain") -----------------
  connectInputBg: '#e4e4e7', // light editable input fill (default-200)
  connectInputText: '#000000', // typed domain value (black)
  connectInputPlaceholder: '#71717a', // "Type your own domain name here"
  connectBtnDisabledBg: '#5b5b61', // disabled "Link this domain" / "Checking…" grey
  connectBtnDisabledText: '#1c1c1f',
  connectCheck: '#17c964', // green validity tick (success)
  connectErrorText: '#f31260', // "domain already used" inline error (danger red; self-derives in light)
  chosenFieldBg: '#353538', // read-only "Chosen domain to link" field (separator-secondary, dark)
  dnsHeading: '#fafafa', // "We can set up…" heading (zinc-50)
  dnsBody: '#d4d4d8', // explanatory paragraph (zinc-300)
  dnsToggle: '#a1a1aa', // "Hide / View DNS Settings" link (zinc-400)
  dnsTableWrapBg: '#232325', // table outer plate (surface-secondary)
  dnsTableBodyBg: '#18181b', // table body (surface)
  dnsHeaderStrong: '#fcfcfc', // "Type" header cell (foreground)
  dnsHeaderMuted: '#a1a1aa', // Name / Data / TTL header cells
  dnsCellStrong: '#fcfcfc', // record cell text
  dnsRowSeparator: '#3c3c3f', // row hairline (tertiary, rendered at 50%)
  dnsCopyIcon: '#a1a1aa', // copy glyph
  dnsColLabel: '#71717a', // mobile card field labels (Host / Value / TTL)
  // Mobile dark editable / read-only field (globe + value + green check).
  mobileFieldBg: '#18181b',

  // Step 3 — logo -------------------------------------------------------------
  logoImageBg: '#27272a', // logo-card image frame fill (foreground-800)
  logoCaption: '#a1a1aa', // logo-card caption, on the DARK plate (default-400)
  // Caption ink when the card plate is LIGHT (a dark/black logo gets the light
  // backing): a near-black so the description reads with strong contrast on the
  // #e9e9ee plate (the `logoCaption` grey is for the dark plate and washes out here).
  // The plate tone is luminance-driven, not theme-driven, so this is pinned in both
  // app themes (see themePalette LIGHT_OVERRIDES).
  logoCaptionInk: '#18181b',
  dropzoneText: '#66686c', // "Click or drop to upload your logo"
  uploadSuccess: '#fafafa', // "Logo uploaded successfully!" heading
  // Uploaded-logo preview plate: the tone flips for contrast against the logo's
  // own luminance (light mark → dark plate, dark mark → light plate), same model
  // as the brand-switcher tile + Website-logos preview. Values match those.
  logoFrameDark: '#101014', // backing for a light/white logo
  logoFrameLight: '#e9e9ee', // backing for a dark/black logo
  // Whole-screen "preparing your logo" scrim while upload + remove-background run.
  logoProcessingScrim: 'rgba(8, 8, 10, 0.72)',
  logoSpinnerTrack: 'rgba(255, 255, 255, 0.16)', // spinner ring track
  // AI-logo "generating" tile hairline (a faint inner top-edge lift on the dark
  // canvas). The tile stays dark in both themes, so this value is theme-pinned.
  genTileBorder: 'rgba(255, 255, 255, 0.07)',
  // The status caption ("Generating your logo…") that types over the field — a
  // fixed cool grey that stays legible on the dark tile in both themes.
  genLabelInk: 'rgba(228, 232, 242, 0.5)',
  // Caption halo: an OPAQUE card-plate colour (matches genPlate's top stop), worn
  // as a solid 5px text-stroke behind the glyphs so no dots touch the text — it
  // reads as the background bleeding through, not an outline.
  genLabelHalo: '#1a1a1e',
  // Light-plate counterparts of the two tokens above, used while the "generating"
  // overlay is still up over a card that has ALREADY swapped to its light plate (a
  // dark logo revealing — the plate flips by luminance before the grid finishes
  // fading). Ink → a soft near-black (mirrors the dark-plate ink's subtle 0.5 alpha);
  // halo → the light plate itself, so it reads as the background bleeding through
  // (mirrors genLabelHalo matching the dark plate). Both pinned in both app themes.
  genLabelInkLight: 'rgba(20, 21, 25, 0.55)',
  genLabelHaloLight: '#e9e9ee',
  // Loaded-logo card plate (light marks): a SOLID dark matching the generating
  // tile, so the luminance-driven light⇄dark swap can cross-fade (a gradient can't).
  genCardDark: '#18181c',

  // Step 4 — deploy / "Go live" -----------------------------------------------
  deployGreen: '#17c964', // "Finish setup" / "Go to dashboard" CTA (success)
  deployGreenText: '#000000', // CTA label (success-foreground)
  deployProgressTrack: 'rgba(0, 224, 255, 0.15)', // deploying progress-bar track (cyan tint)
  deployTrackReview: 'rgba(174, 166, 241, 0.08)', // review progress-bar track (lilac tint)
  deployStepText: '#a1a1aa', // deploying completed-step label (default-400)
  deployStepFinished: '#e4e4e7', // finished completed-step label (default-200)
  deployCompletedLabel: '#74dfa2', // "Completed" header label (success-300)
  deployCheck: '#17c964', // completed-step check
  summaryStrong: '#a1a1aa', // "Done so far" / "Summary" section heading
  liveDot: '#17c964', // "Live on {domain}" status dot
  deviceFallbackBg: '#000000', // device display + notch/chin fallback (when no theme token)
  deployingBlurBg: 'rgba(0, 0, 0, 0.35)', // deploying screen-overlay wash (over the blur)
} as const;

export const CUST = themed('CUST', CUST_RAW);

/**
 * Device-screen overlay box-shadows for the deploy phases (inset vignette / glow).
 * Effect strings with embedded colours live here so component files stay literal-free.
 */
const CUST_SHADOWS_RAW = {
  // Pending ("locked") — dark inner vignette, cyan glow primed at 0 alpha.
  pendingVignetteDesktop:
    'inset 0 0 400px 40px rgba(0, 0, 0, 0.5), inset 0 0 220px 20px rgba(0, 224, 255, 0), inset 0 0 40px 0 rgba(0, 224, 255, 0)',
  pendingVignettePhone:
    'inset 0 0 400px 40px rgba(0, 0, 0, 0.5), inset 0 0 120px 20px rgba(0, 224, 255, 0), inset 0 0 40px 0 rgba(0, 224, 255, 0)',
  // Deploying — magenta→cyan inner glow.
  deployingGlowDesktop:
    'inset 0 0 400px 40px rgba(255, 0, 212, 0.55), inset 0 0 220px 20px rgba(0, 224, 255, 0.65), inset 0 0 40px 0 rgba(0, 224, 255, 0.7)',
  deployingGlowPhone:
    'inset 0 0 200px 40px rgba(255, 0, 212, 0.55), inset 0 0 120px 20px rgba(0, 224, 255, 0.65), inset 0 0 40px 0 rgba(0, 224, 255, 0.7)',
  // Intro→progress morph: a faint warm comet trailing the leading orange "hero"
  // box as it flies up, fully gone before it lands matte in the rail.
  morphHeroGlow: '0 0 18px 2px rgba(255, 121, 27, 0.42)',
  morphHeroGlowSoft: '0 0 4px 0 rgba(255, 121, 27, 0)',
  // AI-logo "generating" tile — a faint outer depth lift (reads on the light page,
  // invisible on the dark one) so the raised card separates from the surface.
  genTileShadow: '0 10px 28px -6px rgba(0, 0, 0, 0.22)',
} as const;

export const CUST_SHADOWS = themed('CUST_SHADOWS', CUST_SHADOWS_RAW);

/** DNS record-type badge colours (mobile record cards). Keyed by record type
 *  with a neutral fallback for less common types (SOA / AAAA / …). */
const CUST_DNS_TYPE_RAW: Record<string, { color: string; bg: string }> = {
  A: { color: '#8070e9', bg: 'rgba(128, 112, 233, 0.12)' },
  AAAA: { color: '#8070e9', bg: 'rgba(128, 112, 233, 0.12)' },
  NS: { color: '#8070e9', bg: 'rgba(128, 112, 233, 0.12)' },
  MX: { color: '#18c964', bg: 'rgba(24, 201, 100, 0.12)' },
  CNAME: { color: '#f5a524', bg: 'rgba(245, 165, 36, 0.12)' },
  TXT: { color: '#ff5fa2', bg: 'rgba(255, 95, 162, 0.12)' },
  SOA: { color: '#a1a1aa', bg: 'rgba(255, 255, 255, 0.08)' },
};

export const CUST_DNS_TYPE = themed('CUST_DNS_TYPE', CUST_DNS_TYPE_RAW);

/** Neutral fallback badge tone for an unmapped DNS record type. */
const CUST_DNS_TYPE_FALLBACK_RAW = { color: '#a1a1aa', bg: 'rgba(255, 255, 255, 0.08)' } as const;

export const CUST_DNS_TYPE_FALLBACK = themed('CUST_DNS_TYPE_FALLBACK', CUST_DNS_TYPE_FALLBACK_RAW);

const CUST_GRADIENTS_RAW = {
  // Page vignette — transparent centre → #080809 at 0.92 darkening the edges.
  pageVignette: `url("data:image/svg+xml;utf8,<svg viewBox='0 0 1440 1024' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23g)' opacity='0.92'/><defs><radialGradient id='g' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(4.4087e-15 51.2 -72 3.1351e-15 720 512)'><stop stop-color='rgba(0,0,0,0)' offset='0'/><stop stop-color='rgba(8,8,9,1)' offset='1'/></radialGradient></defs></svg>")`,
  // Right panel surface: 6% white wash over a 64% near-black plate.
  panel:
    'linear-gradient(90deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.06) 100%), linear-gradient(90deg, rgba(3, 3, 6, 0.64) 0%, rgba(3, 3, 6, 0.64) 100%)',
  // Selected suggestion card/row — the solid card surface with an orange radial
  // glow rising from the bottom-right corner (Figma "Frame 427318912").
  suggestionSelected:
    'radial-gradient(130% 130% at 100% 95%, rgba(255, 141, 40, 0.3) 0%, rgba(255, 141, 40, 0) 60%), linear-gradient(#141417, #141417)',
  // Ask Lumio label — cyan→blue, painted via bg-clip-text.
  lumioText: 'linear-gradient(-79deg, #00e0ff 1.3%, #5c8fef 100%)',
  // Mobile headings (intro + step titles) — lilac→mauve→tan, via bg-clip-text
  // (Figma Satoshi Black; same sweep as the desktop back-header title).
  headingGradient: 'linear-gradient(90deg, #aea6f1 0%, #cec1cc 50%, #da9c74 100%)',
  // Bottom scrim behind the fixed mobile CTA — content fades into the page bg.
  mobileFooterScrim: 'linear-gradient(to top, #08080a 62%, rgba(8, 8, 10, 0))',
  // Sticky "Connect with {provider}" footer on the DNS card — 8% white wash over
  // a near-black (#0d0c10) plate.
  connectFooter:
    'linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), linear-gradient(90deg, #0d0c10 0%, #0d0c10 100%)',
  // Step 4 live-progress bar fill + the in-progress label (magenta→cyan).
  deployProgress: 'linear-gradient(192deg, #ff00d4 5%, #00e0ff 95%)',
  deployProgressText: 'linear-gradient(37deg, rgba(255, 0, 212, 0.75) 5%, rgba(0, 224, 255, 0.75) 95%)',
  // Glossy sheen that sweeps across the live progress fill while deploying (mirrors
  // the configurator-v3 pack-finder scan sheen). A soft translucent-white streak on
  // a 200%-wide track so `background-position` can slide it over the magenta→cyan
  // fill; transparent shoulders keep it a glint, not a wipe. It always rides ON the
  // coloured fill (never the panel), so it stays white in BOTH themes — pinned in
  // themePalette so the light derivation never flips it to a dark hairline.
  deploySheen: 'linear-gradient(100deg, transparent 40%, rgba(255, 255, 255, 0.35) 50%, transparent 60%)',
  // Deploy right-sidebar surface — layered near-black plate (Figma "Sidebar").
  deploySidebar:
    'linear-gradient(90deg, rgba(0, 0, 0, 0.32) 0%, rgba(0, 0, 0, 0.32) 100%), linear-gradient(-1.7deg, rgba(0, 0, 0, 0.48) 0.6%, rgba(35, 31, 32, 0.48) 99.4%), linear-gradient(90deg, #18181b 0%, #18181b 100%)',
  // Deploy left preview container ("Sidebar - Finish") — transparent → near-black,
  // left to right (darkens the side the MacBook bleeds off).
  previewPanel: 'linear-gradient(90deg, rgba(16, 15, 19, 0) 0%, #100f13 100%)',
  // Pending ("locked") screen scrim — desktop (270°) + phone (11°) variants.
  lockedScrimDesktop:
    'linear-gradient(270deg, #040404 0%, rgba(3, 3, 6, 0.48) 100%), rgba(204, 204, 204, 0.24)',
  lockedScrimPhone:
    'linear-gradient(11deg, #030303 8.98%, rgba(15, 10, 15, 0.96) 38.25%, rgba(3, 3, 6, 0.47) 82.16%)',
  // Deploying — soft edge-feather mask for the animated dot field that fronts the
  // blurred screen (replaces the old static atlas-mask SVG). Alpha-only (mask reads
  // the alpha channel): opaque across the centre, fading to nothing toward the
  // corners so the field never hard-cuts at the screen border. Theme-agnostic — the
  // deploy cinematic stays dark in both themes — so it's pinned identical in light.
  deployGridMask: 'radial-gradient(120% 120% at 50% 50%, #000 40%, rgba(0, 0, 0, 0) 96%)',
  // Step 3 — AI logo "generating" tile (the placeholder that streams in while
  // Lumio renders a suggestion). A flat dark "render canvas" card; the energy is
  // drawn INSIDE it as a monochrome dot field (see GeneratingCard's canvas), so
  // the surface itself is just a calm raised-card plate. Stays dark in both themes
  // (an intentional dark AI viewport, like the deploy cinematic) — pinned in
  // themePalette.
  genPlate: 'linear-gradient(180deg, #1a1a1e 0%, #141418 100%)',
} as const;

export const CUST_GRADIENTS = themed('CUST_GRADIENTS', CUST_GRADIENTS_RAW);

/**
 * Fallback seed colours for AI logo generation when a session has no
 * `configuration_data.tokens` (some sessions reach the customizer without the
 * pre-payment configurator's theme). `/art/logo/generate` REQUIRES colours (it
 * 422s on empty/missing), so without this the whole AI-logo step silently no-ops.
 * This is API request DATA (not CSS), so it stays a plain theme-agnostic literal.
 */
export const CUST_LOGO_SEED_FALLBACK = {
  primaryColor: '#ff6600',
  secondaryColor: '#40b840',
  background: '#18181b',
} as const;

/**
 * Dot ink for the AI-logo "generating" field. The tiles render a monochrome dot
 * grid on a `<canvas>` (each dot's radius + opacity ramps with a slow drifting
 * field), so the colour is applied imperatively, NOT through a CSS var — it's a
 * solid fill modulated per-dot via `globalAlpha`. A cool near-white that stays
 * fixed in both themes (the tile is a dark immersive AI viewport either way).
 */
export const CUST_GEN_DOT_FILL = 'rgb(226, 230, 240)';

/**
 * Dot ink for the same field while it overlays a card that has SWAPPED to its light
 * plate (a dark logo revealing). A near-black mirroring the dark-plate fill above so
 * the dots read on the light backing instead of vanishing into it. Like its sibling
 * this is a canvas fill (not a CSS var) and is luminance-pinned (frame-driven, not
 * theme-driven), so it stays fixed in both app themes.
 */
export const CUST_GEN_DOT_FILL_LIGHT = 'rgb(24, 24, 28)';

export const CUST_ANIM = {
  /** World-map pan/zoom to the selected country (source GSAP 0.6s power1.inOut). */
  MAP_ZOOM_MS: 600,
  MAP_EASE: 'cubic-bezier(0.4, 0, 0.2, 1)',
  /** Hover / press micro-interactions. */
  INTERACT_MS: 150,
  /** Mobile screen slide between sub-steps. */
  SCREEN_MS: 280,
  SCREEN_EASE: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** Intro → progress-bar morph ("Take your website live" dismiss). The four
   *  stacked stage boxes fly up and flatten into the top progress cluster; the
   *  hero (orange) box leads and the globe anchors. Tuned for a single, calm,
   *  jank-free gesture — one shared ease (SCREEN_EASE) governs every flight. */
  MORPH_FLIGHT_MS: 560, // each box's flight + flatten
  MORPH_HERO_DELAY_MS: 60, // the hero box's head start
  MORPH_STAGGER_MS: 45, // per-box cascade after the hero (left→right zip into the rail)
  MORPH_GLYPH_FADE_MS: 150, // numeral fade-out (clears before the box flattens below it)
  MORPH_GLOW_MS: 300, // hero glow-trail
  MORPH_TEXT_OUT_MS: 220, // intro title / subtitle / labels / CTA clearing up-and-out
  MORPH_REVEAL_MS: 360, // intro plate fading to reveal the step beneath (cross-dissolve)
  MORPH_TAIL_MS: 40, // buffer after the last box lands before the real-rail handoff
  MORPH_OUT_EASE: 'cubic-bezier(0.4, 0, 1, 1)', // accelerate-out, for clearing content
  /** Wizard step slide-in (country↔brand↔logo↔deploy, plus the intro→country
   *  entry): forward arrives from the right, back from the left. Mirrors the
   *  sibling configurator's step slide. The morph rail lives in the fixed top bar,
   *  so it floats above this sliding step body untouched. */
  STEP_SLIDE_MS: 340,
  STEP_SLIDE_OFFSET_PX: 28,
  /** Sticky CTA cross-fade between steps: the enabled⇄disabled fill and the label
   *  fade smoothly instead of hard-swapping orange→grey→orange. */
  CTA_FADE_MS: 240,
  /** Deploy preview-screen overlay cross-fade between phases. */
  OVERLAY_MS: 800,
  /** "Done so far" checklist rows easing in as each deploy step streams over the
   *  websocket. A gentle fade + small rise (the check settles a beat later) so a
   *  newly-completed step glides in instead of popping. Deliberately understated:
   *  the reveal is felt, not watched. */
  STEP_REVEAL_MS: 440,
  STEP_REVEAL_SHIFT_PX: 6,
  STEP_CHECK_MS: 340,
  STEP_CHECK_DELAY_MS: 120,
  /** One full rotation of the logo-processing spinner. */
  SPIN_MS: 900,
  /** One pass of the loading-skeleton shimmer. Deliberately slow: a calm, barely-
   *  there sheen that reads as "loading" without pulling the eye (felt, not watched). */
  SKELETON_SHIMMER_MS: 4200,
  /** One pass of the glossy sheen sweeping across the live progress-bar fill.
   *  Slightly slower than the pack-finder scan (2s) so the deploy glint reads as a
   *  calm, periodic shine rather than a busy strobe (felt, not watched). */
  DEPLOY_SHEEN_MS: 2400,
  /** Rotating "Lumio is working…" deploy status line that replaces the static
   *  "Done so far" heading: per-char type / erase speeds, the hold once a phrase is
   *  fully typed, and the gap before the next one cycles in. Keeps the panel feeling
   *  alive in the seconds before the first websocket step lands (when an empty bar +
   *  empty checklist otherwise read as stuck). The caret blinks on its own cadence. */
  STATUS_TYPE_CHAR_MS: 42,
  STATUS_ERASE_CHAR_MS: 24,
  STATUS_HOLD_MS: 1600,
  STATUS_GAP_MS: 360,
  STATUS_CARET_MS: 1000,
} as const;

/* eslint-enable quotes */